import { ApplicationConfig, importProvidersFrom, provideAppInitializer, inject, provideZonelessChangeDetection } from '@angular/core';
import { provideRouter, withEnabledBlockingInitialNavigation } from '@angular/router';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserModule, provideClientHydration } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ClientWsGalavailModule } from 'client-ws-galavail';
import { GalavailRestAuthService } from 'client-ws-galavail';
import { NgWebflowService } from 'webflow-lib';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { CreditCardLibModule } from 'credit-card-lib';
import { StoreModule } from '@ngrx/store';
import { LocalStorageService } from 'angular-2-local-storage';

import { routes } from './app-routing.module';
import { environment } from '../environments/environment';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { ServiceUnavailableInterceptor } from './core/interceptors/service-unavailable.interceptor';
import { LayoutModule } from './layout/layout.module';
import { AuthTokenFacade } from './modules/auth/application/auth-token.facade';
import { NoOpGalavailRestAuthService } from './core/services/no-op-galavail-rest-auth.service';
import './core/services/ngx-bootstrap-compat';

function initializeAuth(): () => Promise<void> {
  return async () => {
    const authTokenFacade = inject(AuthTokenFacade);
    if (typeof window === 'undefined') { return; }
    const token = authTokenFacade.getToken();
    if (token && authTokenFacade.isTokenValid(token)) { return; }
    try {
      await new Promise<void>((resolve) => {
        const timeout = setTimeout(() => { console.warn('Timeout al obtener token'); resolve(); }, 8000);
        authTokenFacade.fetchToken().subscribe({
          next: (resp) => {
            clearTimeout(timeout);
            if (resp && resp.accessToken) {
              authTokenFacade.setToken(resp.accessToken);
            }
            resolve();
          },
          error: () => { clearTimeout(timeout); console.warn('No se pudo obtener el token'); resolve(); }
        });
      });
    } catch { /* no bloquear la app */ }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideClientHydration(),
    provideAppInitializer(initializeAuth()),
    provideRouter(routes, withEnabledBlockingInitialNavigation()),
    importProvidersFrom(
      BrowserModule, BrowserAnimationsModule, ClientWsGalavailModule,
      NgbModule, CreditCardLibModule, LayoutModule,
      StoreModule.forRoot({}, {})
    ),
    // webflow-lib (sucesora de ng-webflow): config runtime desde el environment
    { provide: NgWebflowService, useFactory: () => new NgWebflowService(environment.webflow) },
    provideHttpClient(withInterceptorsFromDi()),
    LocalStorageService,
    { provide: GalavailRestAuthService, useClass: NoOpGalavailRestAuthService },
    // ServiceUnavailableInterceptor primero (outermost): ve también los errores
    // de AuthInterceptor.ensureValidToken() que ocurren antes de next.handle().
    { provide: HTTP_INTERCEPTORS, useClass: ServiceUnavailableInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
  ],
};
